import React, { useContext } from 'react';
import RCTooltip from 'rc-tooltip';

import BodyText from '../Typography/BodyText/BodyText';
import positions from './positions';
import styles from './Tooltip.module.scss';
import { CommonProps } from '../Element/Element';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { ContainerContext } from '../../shared/utils/contexts';

const triggerOptions = ['click', 'hover', 'focus'] as const;

export type TriggerOption = ArrayElementType<typeof triggerOptions>;

const placementOptions = [
  'topCenter',
  'bottomCenter',
  'right',
  'left',
  'topStart',
  'topEnd',
  'bottomStart',
  'bottomEnd',
] as const;

export type PlacementOption = ArrayElementType<typeof placementOptions>;

type TooltipProps = {
  /** Content to be shown inside the tooltip */
  tooltipContent: NonNullable<React.ReactNode>;
  /** Defines how tooltip should be triggered. Can be a string or array of stings*/
  trigger?: TriggerOption | TriggerOption[];
  /** In which position the tooltip should appear */
  placement?: PlacementOption;
  /** Target node for the tooltip */
  children: React.ReactElement;
  /** Additional className added to tooltip overlay */
  overlayClassName?: string;
  /** By passing getTooltipContainer tooltips container will be overriden.
   * Without configuration, it is normally html body but inside ModalDialog it is ModalDialogs inner container */
  getTooltipContainer?: (node: HTMLElement) => HTMLElement;
} & CommonProps<'div'>;

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  tooltipContent,
  children,
  trigger = 'click',
  placement = 'right',
  getTooltipContainer,
  ...props
}: TooltipProps) => {
  if (typeof tooltipContent === 'string') {
    tooltipContent = (
      <BodyText tagName="span" size="sm" block>
        {tooltipContent}
      </BodyText>
    );
  }
  const { getContainer } = useContext(ContainerContext);
  return (
    <RCTooltip
      trigger={trigger}
      overlay={tooltipContent}
      placement={placement}
      destroyTooltipOnHide
      overlayClassName={styles.tooltip}
      prefixCls="telia-react-tooltip"
      align={positions[placement]}
      getTooltipContainer={getTooltipContainer ?? getContainer}
      {...props}
    >
      {children}
    </RCTooltip>
  );
};

// Aliases for backwards compatibility
export type TooltipPlacement = PlacementOption;
export type TooltipTrigger = TriggerOption;

export { triggerOptions, placementOptions };
export default Tooltip;
